/**
 * How a copied gateway, list, list group or campaign found its match in this country, as a calm
 * dot + word (27/10/2026; v2 26/10): same gateway · same name · remembered · only one — found by
 * itself; near match — used, to confirm; picked · created here · left out; "no match" in the
 * warning tone while nobody has answered for it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { GroupCopyMatch } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

export type BadgeTone = 'ok' | 'check' | 'chosen' | 'out' | 'warning';

export interface MatchBadgeSpec {
  label: string;
  tone: BadgeTone;
  /** What the label means, on hover. */
  title?: string;
}

const SURE: Partial<Record<GroupCopyMatch, MatchBadgeSpec>> = {
  'same-gateway': { label: 'same gateway', tone: 'ok', title: 'The same SMSWiser gateway in both countries' },
  'same-name': { label: 'same name', tone: 'ok', title: 'The same name, the country left out' },
  remembered: { label: 'remembered', tone: 'ok', title: 'Picked in an earlier copy between these two countries' },
  only: { label: 'only one', tone: 'ok', title: "The target group's only campaign" },
};

export function matchBadge(how: GroupCopyMatch, hasTarget: boolean, answered: boolean): MatchBadgeSpec {
  if (how === 'none' && !answered) return { label: 'no match', tone: 'warning', title: 'Nothing like it in this country — pick one, or it stays out' };
  if (how === 'likely' && !answered) return { label: 'near match', tone: 'check', title: 'A near name — used; confirm it or pick another' };
  if (!hasTarget) return { label: 'left out', tone: 'out' };
  const sure = SURE[how];
  if (sure) return sure;
  return how === 'create' ? { label: 'created here', tone: 'chosen' } : { label: 'picked', tone: 'chosen', title: 'Picked in this review — remembered for the next copy' };
}

const TONE: Record<BadgeTone, { text: string; dot: string }> = {
  ok: { text: 'text-muted-foreground', dot: 'bg-success' },
  check: { text: 'text-warning', dot: 'bg-warning' },
  chosen: { text: 'text-primary', dot: 'bg-primary' },
  out: { text: 'text-muted-foreground', dot: 'bg-muted-foreground/40' },
  warning: { text: 'text-warning', dot: 'bg-warning' },
};

export const MatchBadge: FC<{ spec: MatchBadgeSpec }> = ({ spec }) => (
  <span title={spec.title} className={cn('inline-flex h-5 shrink-0 items-center gap-1.5 whitespace-nowrap text-[11px] font-medium', TONE[spec.tone].text)}>
    <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', TONE[spec.tone].dot)} />
    {spec.label}
  </span>
);
